import { useState, useCallback, useMemo } from 'react'
import { ipfs } from '@/services/index.ts'
import type {
  IPFSUploadResult,
  IPFSUploadOptions,
} from '@/services/ipfs/ipfs.types.ts'

interface UseIPFSState {
  loading: boolean
  error: string | null
}

interface UseIPFSReturn extends UseIPFSState {
  uploadFile: (
    file: File | Blob,
    options?: IPFSUploadOptions,
  ) => Promise<IPFSUploadResult>
  uploadText: (
    text: string,
    filename?: string,
    options?: IPFSUploadOptions,
  ) => Promise<IPFSUploadResult>
  uploadJSON: (
    data: Record<string, unknown>,
    options?: IPFSUploadOptions,
  ) => Promise<IPFSUploadResult>
  downloadText: (cid: string) => Promise<string | null>
  downloadFile: (cid: string) => Promise<Blob | null>
  downloadJSON: <T = Record<string, unknown>>(cid: string) => Promise<T | null>
  getURL: (cid: string) => Promise<string | null>
  remove: (id: string) => Promise<boolean>
}

export function useIPFS(): UseIPFSReturn {
  const [state, setState] = useState<UseIPFSState>({
    loading: false,
    error: null,
  })

  const setLoading = () => setState({ loading: true, error: null })
  const setError = (error: string) => setState({ loading: false, error })
  const setDone = () => setState((s) => ({ ...s, loading: false }))

  const service = useMemo(() => ipfs, [])

  const uploadFile = useCallback(
    async (
      file: File | Blob,
      options?: IPFSUploadOptions,
    ): Promise<IPFSUploadResult> => {
      setLoading()
      try {
        const result = await service.uploadFile(file, options)
        setDone()
        return result
      } catch (e) {
        const msg = e instanceof Error ? e.message : 'File upload failed'
        setError(msg)
        throw new Error(msg)
      }
    },
    [service],
  )

  const uploadText = useCallback(
    async (
      text: string,
      filename?: string,
      options?: IPFSUploadOptions,
    ): Promise<IPFSUploadResult> => {
      setLoading()
      try {
        const result = await service.uploadText(text, filename, options)
        setDone()
        return result
      } catch (e) {
        const msg = e instanceof Error ? e.message : 'Text upload failed'
        setError(msg)
        throw new Error(msg)
      }
    },
    [service],
  )

  const uploadJSON = useCallback(
    async (
      data: Record<string, unknown>,
      options?: IPFSUploadOptions,
    ): Promise<IPFSUploadResult> => {
      setLoading()
      try {
        const result = await service.uploadJSON(data, options)
        setDone()
        return result
      } catch (e) {
        const msg = e instanceof Error ? e.message : 'JSON upload failed'
        setError(msg)
        throw new Error(msg)
      }
    },
    [service],
  )

  const downloadText = useCallback(
    async (cid: string): Promise<string | null> => {
      setLoading()
      try {
        const result = await service.downloadText(cid)
        setDone()
        return result
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Text download failed')
        return null
      }
    },
    [service],
  )

  const downloadFile = useCallback(
    async (cid: string): Promise<Blob | null> => {
      setLoading()
      try {
        const result = await service.downloadFile(cid)
        setDone()
        return result
      } catch (e) {
        setError(e instanceof Error ? e.message : 'File download failed')
        return null
      }
    },
    [service],
  )

  const downloadJSON = useCallback(
    async <T = Record<string, unknown>>(cid: string): Promise<T | null> => {
      setLoading()
      try {
        const result = await service.downloadJSON<T>(cid)
        setDone()
        return result
      } catch (e) {
        setError(e instanceof Error ? e.message : 'JSON download failed')
        return null
      }
    },
    [service],
  )

  const getURL = useCallback(
    async (cid: string): Promise<string | null> => {
      try {
        return await service.getURL(cid)
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Failed to get URL')
        return null
      }
    },
    [service],
  )

  const remove = useCallback(
    async (id: string): Promise<boolean> => {
      setLoading()
      try {
        await service.delete(id)
        setDone()
        return true
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Delete failed')
        return false
      }
    },
    [service],
  )

  return {
    ...state,
    uploadFile,
    uploadText,
    uploadJSON,
    downloadText,
    downloadFile,
    downloadJSON,
    getURL,
    remove,
  }
}
